import type { RealtimeTransportAdapter, TransportEventHandler } from "../types"

const HEARTBEAT_TIMEOUT_MS = 45_000
const INITIAL_RECONNECT_MS = 1_000
const MAX_RECONNECT_MS = 30_000

const E2E_LOG =
  (import.meta as { env?: { VITE_E2E?: string } }).env?.VITE_E2E === "1"

export class SSETransport implements RealtimeTransportAdapter {
  private eventSource: EventSource | null = null
  private handlers = new Set<TransportEventHandler>()
  private _connected = false
  private url: string | null = null
  private resourceNames: string[] = []
  private getCursors: (() => Record<string, number>) | null = null
  private getAuthToken: (() => string | null) | null = null
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectDelay = INITIAL_RECONNECT_MS
  private hasConnectedOnce = false

  get connected(): boolean {
    return this._connected
  }

  connect(
    url: string,
    resourceNames?: string[],
    getCursors?: () => Record<string, number>,
    getAuthToken?: () => string | null
  ): void {
    this.url = url
    this.resourceNames = resourceNames ?? []
    // Resolved on each (re)connect so reconnects send the *current* cursor
    // snapshot, not the one captured at first connect — otherwise a long
    // session would replay from boot every reconnect.
    this.getCursors = getCursors ?? null
    // Likewise resolved per-connect: a token rotated mid-session (e.g. a
    // password change that revokes the prior JWT) must reach the next
    // reconnect, or the stream loops offline with the stale token.
    this.getAuthToken = getAuthToken ?? null
    this.doConnect()
  }

  onEvent(handler: TransportEventHandler): void {
    this.handlers.add(handler)
  }

  offEvent(handler: TransportEventHandler): void {
    this.handlers.delete(handler)
  }

  disconnect(): void {
    this.cleanup()
    this.url = null
    this.hasConnectedOnce = false
  }

  private doConnect(): void {
    if (!this.url) return
    this.cleanup()

    const es = new EventSource(this.buildUrl(), { withCredentials: true })
    this.eventSource = es

    es.onopen = () => {
      this._connected = true
      this.reconnectDelay = INITIAL_RECONNECT_MS
      this.resetHeartbeatTimer()
      if (this.hasConnectedOnce) {
        this.emit("__reconnect", null)
      }
      this.hasConnectedOnce = true
    }

    es.onerror = () => {
      this._connected = false
      this.emit("__disconnect", null)
      this.cleanup()
      this.scheduleReconnect()
    }

    // Listen for unnamed events (fallback)
    es.onmessage = (event) => {
      this.resetHeartbeatTimer()
      this.handleMessage("message", event.data)
    }

    // Listen for heartbeat named events
    es.addEventListener("heartbeat", () => {
      this.resetHeartbeatTimer()
    })

    // Scope-version control frames (STK-1238): `scope` at handshake (the
    // derived version for mismatch detection) and `scope-changed` mid-session
    // (live membership change → wipe + cold re-pull). Same parse+emit shape
    // as resource events, so reuse the listener helper; the engine demuxes
    // them BEFORE its resource lookup.
    for (const name of ["scope", "scope-changed"]) {
      this.listenForResource(name)
    }

    // Listen for each resource's named events
    for (const name of this.resourceNames) {
      this.listenForResource(name)
    }
  }

  /** Add a named SSE event listener for a specific resource */
  listenForResource(resourceName: string): void {
    if (!this.eventSource) return
    this.eventSource.addEventListener(resourceName, (event) => {
      this.resetHeartbeatTimer()
      try {
        const data = JSON.parse((event as MessageEvent).data as string)
        this.handleMessage(resourceName, data)
      } catch {
        this.handleMessage(resourceName, (event as MessageEvent).data)
      }
    })
  }

  private handleMessage(event: string, rawData: unknown): void {
    let data = rawData
    if (typeof rawData === "string") {
      try {
        data = JSON.parse(rawData)
      } catch {
        // Keep as string
      }
    }
    this.emit(event, data)
  }

  private emit(event: string, data: unknown): void {
    if (!E2E_LOG) {
      // Stringify so the full payload survives capture pipelines (Playwright
      // trace, copy-paste from devtools) that otherwise elide nested objects
      // as `{…}`. The live object is still passed alongside for interactive
      // expansion in browser devtools.
      // eslint-disable-next-line no-console
      console.log("[SSE]", event, data, JSON.stringify(data))
    }
    for (const handler of this.handlers) {
      handler(event, data)
    }
  }

  private resetHeartbeatTimer(): void {
    if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer)
    this.heartbeatTimer = setTimeout(() => {
      // No heartbeat received — assume dead connection
      this._connected = false
      this.emit("__disconnect", null)
      this.cleanup()
      this.scheduleReconnect()
    }, HEARTBEAT_TIMEOUT_MS)
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.url) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, MAX_RECONNECT_MS)
      this.doConnect()
    }, this.reconnectDelay)
  }

  /**
   * Append `?cursor=resource:syncId` query params for every resource the
   * client has registered. The server uses this list as the connection's
   * live-event subscription set AND replays any missing deltas via batched
   * SSE events on connect, replacing the burst of `GET /{resource}/changes`
   * calls we used to fan out at boot.
   *
   * `syncId === 0` entries are included on purpose — an empty resource
   * (cold-start returned no rows) still needs to be subscribed so live
   * events fire when the first row is created. Filtering them out would
   * leave the connection deaf to those resources until a reconnect.
   */
  private buildUrl(): string {
    if (!this.url) return ""
    const params: string[] = []

    // Token first so it's resolved fresh on every (re)connect, not frozen at
    // first connect — see `getAuthToken` on connect().
    const token = this.getAuthToken?.() ?? null
    if (token) params.push(`token=${encodeURIComponent(token)}`)

    const cursors = this.getCursors?.() ?? {}
    for (const [resource, syncId] of Object.entries(cursors)) {
      if (Number.isFinite(syncId) && syncId >= 0) {
        params.push(`cursor=${encodeURIComponent(`${resource}:${syncId}`)}`)
      }
    }

    if (params.length === 0) return this.url
    const sep = this.url.includes("?") ? "&" : "?"
    return `${this.url}${sep}${params.join("&")}`
  }

  private cleanup(): void {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this._connected = false
  }
}
